import { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";

import { useAppState } from "core/contexts/AppContext";
import useToast from "core/hooks/useToast";
import { persistValue, removeItem, retrieveValue } from "core/includes/localStorage";
import { DELETE_CART, GET_CART, UPDATE_OR_CREATE_CART } from "core/includes/queries";
import { ORDER_DISCOUNT_MESSAGE } from "pages/Components/ProductListPage/includes/constants";
import { getProductFromCart } from "pages/includes/cart";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";
import { getCanRenderWebp } from "core/includes/image";

const CART_LOCAL_STORAGE_KEY = 'cart';

export type UpdateCartFunction = (args: {
    productId?: number,
    productQuantity?: number,
    discountCode?: string,
    registrationId?: string | null,
    userCode?: string | null,
    isEvent?: boolean,
    countryCode?: string,
}) => any;

const useCart = () => {
    const [appState, setAppState] = useAppState();
    const [updateOrCreateCart] = useMutation(UPDATE_OR_CREATE_CART);
    const [deleteCart] = useMutation(DELETE_CART);
    const [successToast, errorToast] = useToast();
    const [getCartDetails] = useLazyQuery(GET_CART, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
    });

    /**
     * Sets the cart from the backend when any new changes are applied (such as added shipping, updated products) in
     * local storage and app state.
     * As part of this, it also makes sure that the registration ID is not lost as it only set when an Event
     * Registration is added to the cart. Note that this is going to simplified in the following ticket: WRT-17352
     *
     * @param {Cart} cart
     * @return {Cart} cart
     */
    const processCart = useCallback((cart: Cart) => {
        persistValue(CART_LOCAL_STORAGE_KEY, cart.orderUuid);

        // It keeps the existing UserRegistrationId and UserCode for each Event by appState.Cart
        cart.products.forEach((cartProduct: CartProduct) => {
            if (cartProduct.type === 'event') {
                if (cartProduct.userCode === null) {
                    cartProduct.userCode = appState.cart?.products?.find((product: CartProduct) => (
                        product.id === cartProduct.id
                    ))?.userCode;
                }
                if (cartProduct.registrationId === null) {
                    cartProduct.registrationId = appState.cart?.products?.find((product: CartProduct) => (
                        product.id === cartProduct.id
                    ))?.registrationId;
                }
            }
        });

        setAppState((prevState: any) => ({
            cart: {
                ...prevState.cart,
                ...cart
            }
        }));

        return cart;
    }, [appState.cart?.products, setAppState]);

    /**
     * Either creates or updates the cart. If cart is not found in the local storage, then it creates it first.
     * The only way to create a cart is to add a product to the cart. After that discount can be applied. A product can
     * be an event or a resource (physical product).
     * Note that this function is used to add AND remove products and discounts based on the passed params.
     *
     * @param {?number} productId
     * @param {number} productQuantity
     * @param {string} discountCode
     * @param {string} registrationId
     * @param {string} userCode
     * @param {boolean} isEvent
     * @param {?string} countryCode
     * @param {?boolean} isOnlineEvent
     *
     * @return {?Cart} cart
     */
    const updateCart: UpdateCartFunction = useCallback(async ({
        productId = null,
        productQuantity = 0,
        discountCode = null,
        registrationId = null,
        userCode = null,
        isEvent = false,
        countryCode = "",
    }) => {
        const orderUuid = retrieveValue(CART_LOCAL_STORAGE_KEY) || "";

        return await updateOrCreateCart({
            variables: {
                orderUuid: appState.cart?.orderUuid || orderUuid,
                productId,
                productQuantity,
                discountCode,
                registrationId,
                userCode,
                isEvent,
                countryCode: countryCode || appState.cart?.countryCode || appState.countryCode?.code,
                canRenderWebp: getCanRenderWebp()
            }
        }).then((data) => {
            const cart = data.data.updateShoppingCart;
            if (!cart && appState.cart && appState.cart.products.length === 1) {
                removeItem(CART_LOCAL_STORAGE_KEY)
                setAppState({
                    cart: null
                })
            } else {
                processCart(cart);
            }

            if (productId !== null) {
                const product = getProductFromCart(appState.cart, productId);

                if (!product || (productQuantity - product.quantity) > 0) {
                    setAppState({ cartMessage: "Item added to your cart" });
                } else if (product && isEvent && productQuantity > 0) {
                    setAppState({ cartMessage: "Item updated in your cart" });
                } else {
                    setAppState({ cartMessage: "Item removed from your cart" });
                }
            }

            if (discountCode !== null) {
                if (discountCode) successToast('Discount applied to your cart.');
                else successToast('Discount removed from your cart.')
            }
            return cart
        }).catch(() => {
            if (discountCode != null) {
                errorToast(ORDER_DISCOUNT_MESSAGE.fail)
            } else {
                errorToast("Failed to update cart");
            }
            return null;
        });
    }, [appState.cart, appState.countryCode?.code, errorToast, processCart, successToast, updateOrCreateCart, setAppState]);

    const clearCart = useCallback(() => {
        const orderUuid = retrieveValue(CART_LOCAL_STORAGE_KEY) ?? "";

        deleteCart({
            variables: {
                orderUuid
            }
        }).then(() => {
            removeItem(CART_LOCAL_STORAGE_KEY)
            setAppState({
                cart: null
            })
        })
    }, [deleteCart, setAppState]);

    const getCart = useCallback(() => {
        const orderUuid = retrieveValue(CART_LOCAL_STORAGE_KEY) ?? "";

        setAppState({
            isCartLoading: true
        });
        let cart = null;
        getCartDetails({
            variables: {
                orderUuid,
                canRenderWebp: getCanRenderWebp()
            }
        }).then(res => {
            cart = res.data?.getCart || null;
            setAppState({ cart, isCartLoading: false });
            if (!cart) {
                removeItem(CART_LOCAL_STORAGE_KEY)
            } else {
                persistValue(CART_LOCAL_STORAGE_KEY, cart?.orderUuid || null);
            }
        });
        return cart;
    }, [getCartDetails, setAppState]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const hasRegistrants = useCallback((productId: number, userCode: string | null) => {
        const cart = appState.cart;

        if (cart) {
            return !!cart.products?.find((product: any) => product.id === productId);
        }

        return false;
    }, [appState.cart]);

    const clearCartStorage = useCallback(() => {
        removeItem(CART_LOCAL_STORAGE_KEY)
        setAppState({
            cart: null
        })
    }, [setAppState]);

    const getLocalStorageCartUuid = () => {
        return retrieveValue(CART_LOCAL_STORAGE_KEY) ?? "";
    }

    const getCountry = () => {
        const country = appState.countries.find((country: CountryCodeItem) =>
            country.code.toUpperCase() === (appState.cart?.countryCode?.toUpperCase() || appState.countryCode.code.toUpperCase()));

        if (!country) {
            return DEFAULT_COUNTRY_CODE_CONFIG;
        }

        return country;
    }

    return {
        updateCart,
        getCart,
        clearCart,
        clearCartStorage,
        processCart,
        getLocalStorageCartUuid,
        getCountry,

        hasRegistrants,
    };
};

export default useCart;